import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Input, Typography } from "@mui/joy";
import { Add, Delete, Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  DataGrid,
  GridCallbackDetails,
  GridColDef,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { Artist } from "types";
import { LinearProgress } from "@mui/material";
import axios from "axios";
import { parseListDate } from "utils/dateParser";
import DGContextMenu from "components/DGContextMenu";
import { StyledCheckbox } from "components/styled_components";
import { useNavigate } from "react-router-dom";
import { useDialogManger } from "context/DialogManagerContext";
import { usePrevious } from "hooks";
import { toast } from "sonner";
import { fetchArtists } from "utils";
import { CustomNoRowsOverlay } from "components/styled_components/DataGridOverlays";
import ArtistDetailsModal from "./DetailsModal";
import { useAuthorization } from "context/AuthorizationContext";

interface ArtistsListProps {}

const ArtistsList: React.FC<ArtistsListProps> = () => {
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [sort, setSort] = useState<GridSortModel>([
    { field: "created_at", sort: "asc" },
  ]);
  const [filter, setFilter] = useState("");
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [selectedRow, setSelectedRow] = useState("");
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { showDialog } = useDialogManger();
  const { isAllowed } = useAuthorization();
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  let filterTimeout = useRef<NodeJS.Timeout>(null!);
  const prevFilter = usePrevious(filter);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedRow(event.currentTarget.getAttribute("data-id")!);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const columns = useMemo<GridColDef<Artist>[]>(
    () => [
      {
        field: "name",
        headerName: `${t("Name")}`,
        sortable: true,
        filterable: false,
        flex: 1,
        hideable: false,
      },
      {
        field: "address",
        headerName: `${t("Address")}`,
        sortable: true,
        filterable: false,
        flex: 1,
        hideable: false,
      },
      {
        field: "vat",
        headerName: `${t("Vat")}`,
        sortable: true,
        filterable: false,
        flex: 1,
        hideable: false,
      },

      {
        field: "created_at",
        headerName: `${t("Created at")}`,
        sortable: true,
        filterable: false,
        hideable: false,
        flex: 1,
        renderCell: ({ row }) => <>{parseListDate(row.createdAt)}</>,
      },
      {
        field: "who_create",
        headerName: `${t("Who create")}`,
        sortable: true,
        filterable: false,
        hideable: false,
        flex: 1,
        renderCell: ({ row }) => (
          <div className="MuiDataGrid-cellContent" title={row.whoCreate}>
            {row.whoCreate}
          </div>
        ),
      },
      {
        field: "updated_at",
        headerName: `${t("Updated at")}`,
        sortable: true,
        filterable: false,
        hideable: false,
        flex: 1,
        renderCell: ({ row }) => (
          <>{row.updatedAt ? parseListDate(row.updatedAt) : ""}</>
        ),
      },
      {
        field: "who_update",
        headerName: `${t("Who update")}`,
        sortable: true,
        filterable: false,
        hideable: false,
        flex: 1,
        renderCell: ({ row }) => (
          <div
            className="MuiDataGrid-cellContent"
            title={row.whoUpdate ? row.whoUpdate : ""}
          >
            {row.whoUpdate}
          </div>
        ),
      },
    ],
    [t]
  );

  const getArtists = useCallback(async () => {
    setLoading(true);
    const data = await fetchArtists({
      page: page + 1,
      limit,
      sort: `${sort[0].field}:${sort[0].sort}`,
      search: filter,
    });
    if (data) {
      setArtists(data.data);
      setTotalItems(data.total);
    }

    setLoading(false);
  }, [filter, limit, page, sort]);

  const onDeleteArtists = async () => {
    try {
      const { status } = await axios.post(`/api/artists/delete`, {
        artistIds: selectedRows,
      });
      if (status === 200) {
        setSelectedRows([]);
        getArtists();
        //Close the context menu when item is deleted
        handleClose();
        toast.success(t("Artists successfully deleted"));
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot delete selected artists"));
      }
    }
    setLoading(false);
  };

  const onDeleteArtistsClick = () => {
    showDialog({
      title: "Delete the selected artists?",
      body: "If you proceed with this action, the selected artists will be deleted.",
      actions: [
        {
          label: "Cancel",
          color: "neutral",
          variant: "plain",
          onClick: (e, closeModal) => {
            closeModal();
          },
        },
        {
          label: "Delete",
          color: "danger",
          variant: "solid",
          onClick: (e, closeModal) => {
            onDeleteArtists();
            closeModal();
          },
        },
      ],
    });
  };

  const onDeleteArtist = async () => {
    try {
      const { status } = await axios.delete(`/api/artists/${selectedRow}`);
      if (status === 200) {
        setSelectedRow("");
        getArtists();
        //Close the context menu when item is deleted
        handleClose();
        toast.success(t("Artist successfully deleted"));
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot delete artist"));
      }
    }
    setLoading(false);
  };

  const onDeleteArtistClick = () => {
    showDialog({
      title: "Delete the selected artist?",
      body: "If you proceed with this action, the selected artist will be deleted.",
      actions: [
        {
          label: "Cancel",
          color: "neutral",
          variant: "plain",
          onClick: (e, closeModal) => {
            closeModal();
          },
        },
        {
          label: "Delete",
          color: "danger",
          variant: "solid",
          onClick: (e, closeModal) => {
            onDeleteArtist();
            closeModal();
          },
        },
      ],
    });
  };

  const onSortChange = (
    model: GridSortModel,
    details: GridCallbackDetails<any>
  ) => {
    if (model.length === 0) {
      setSort([{ field: "created_at", sort: "asc" }]);
    } else {
      setSort(model);
    }
  };

  useEffect(() => {
    if (prevFilter !== filter && prevFilter !== undefined) {
      if (filterTimeout.current) {
        clearTimeout(filterTimeout.current);
      }
      filterTimeout.current = setTimeout(() => {
        getArtists();
      }, 500);

      return () => {
        clearTimeout(filterTimeout.current);
      };
    } else {
      getArtists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sort, limit, filter, getArtists]);

  return (
    <Box
      width={"100%"}
      height={"100%"}
      display="flex"
      flexDirection="column"
      rowGap={2}
    >
      <Typography level="h4" fontWeight={600}>
        {t("Artists")}
      </Typography>
      <Box
        display="flex"
        alignSelf="flex-end"
        flexDirection="row"
        width={"100%"}
      >
        <Box
          display="flex"
          alignSelf="flex-end"
          flexDirection="row"
          justifyContent="flex-start"
          flex={1}
          columnGap={2}
        >
          {isAllowed("artists.write") ? (
            <Button
              variant="solid"
              startDecorator={<Add />}
              color="primary"
              size="sm"
              onClick={() => navigate("new")}
            >
              {t("New")}
            </Button>
          ) : null}
          {isAllowed("artists.delete") ? (
            <Button
              variant="soft"
              startDecorator={<Delete />}
              color="danger"
              size="sm"
              disabled={selectedRows.length === 0}
              onClick={onDeleteArtistsClick}
            >
              {t("Delete")}
            </Button>
          ) : null}
        </Box>
        <Box
          display="flex"
          alignSelf="flex-end"
          flexDirection="row"
          justifyContent="flex-end"
          flex={1}
        >
          <Input
            size="sm"
            placeholder={`${t("Search")}...`}
            startDecorator={<Search />}
            fullWidth
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            sx={{ flex: "0.60 1" }}
          ></Input>
        </Box>
      </Box>

      <Box
        alignSelf="stretch"
        flex={"1 1"}
        maxHeight={"calc( 100% - 100px )"}
        boxSizing={"border-box"}
      >
        <DataGrid
          columns={columns}
          rows={artists}
          checkboxSelection
          pagination
          paginationModel={{ page: page, pageSize: limit }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setLimit(model.pageSize);
          }}
          disableColumnMenu
          density="compact"
          sortingMode="server"
          paginationMode="server"
          sortModel={sort}
          rowCount={totalItems}
          onSortModelChange={onSortChange}
          onRowSelectionModelChange={(selRows) => {
            setSelectedRows(selRows);
          }}
          localeText={{
            columnHeaderSortIconLabel: `${t("Sort")}`,
            noRowsLabel: `${t("No artists found")}`,
            MuiTablePagination: {
              labelDisplayedRows: ({ from, to, count }) =>
                t("{{from}}-{{to}} of {{count}}", {
                  from,
                  to,
                  count,
                }),
              labelRowsPerPage: `${t("Rows per page")}:`,
            },
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
          }}
          slots={{
            loadingOverlay: LinearProgress,
            baseCheckbox: StyledCheckbox,
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          slotProps={{
            loadingOverlay: {
              color: "primary",
            },
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
            },
          }}
          loading={loading}
        />
        <DGContextMenu
          resource="artists"
          rowId={selectedRow}
          open={contextMenu !== null}
          onClose={handleClose}
          onDetail={() => {
            setDetailsOpen(true);
            handleClose();
          }}
          onEdit={() => {
            navigate(selectedRow);
          }}
          onDelete={onDeleteArtistClick}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            root: {
              onContextMenu: (e) => {
                e.preventDefault();
                handleClose();
              },
            },
          }}
        />
        <ArtistDetailsModal
          open={detailsOpen}
          artistId={selectedRow}
          onClose={() => setDetailsOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default ArtistsList;
